import { SavingsData, SavingEntry } from '../types';
import { supabase } from './supabaseClient';

// Load savings data from Supabase
export const loadSavingsData = async (): Promise<SavingsData> => {
  console.log("Loading data from Supabase");
  
  try {
    // Query all entries from the savings_entries table
    const { data, error } = await supabase
      .from('savings_entries')
      .select('*')
      .order('date', { ascending: false });
      
    if (error) {
      throw error;
    }
    
    console.log("Data loaded from Supabase:", data);
    
    // Transform the data to match our SavingsData format
    const entries: SavingEntry[] = data.map(item => ({
      id: item.id,
      department: item.department,
      description: item.description,
      amount: Number(item.amount), // Ensure amount is a number
      date: item.date,
      type: item.type as 'recurring' | 'one-time',
      vendor: item.vendor || undefined
    }));
    
    return {
      entries,
      lastUpdated: new Date().toISOString()
    };
  } catch (error) {
    console.error("Error loading data from Supabase:", error);
    
    // Return empty data structure on error
    return {
      entries: [],
      lastUpdated: new Date().toISOString()
    };
  }
};

// Add a new savings entry to Supabase
export const addSavingEntry = async (entry: Omit<SavingEntry, 'id'>): Promise<void> => {
  console.log("Adding new entry to Supabase:", entry);
  
  try {
    // Insert the new entry
    const { data, error } = await supabase
      .from('savings_entries')
      .insert([
        {
          department: entry.department,
          description: entry.description,
          amount: entry.amount,
          date: entry.date,
          type: entry.type,
          vendor: entry.vendor || null
        }
      ]);
      
    if (error) {
      throw error;
    }
    
    console.log("Entry added successfully:", data);
  } catch (error) {
    console.error("Error adding entry to Supabase:", error);
    throw error;
  }
};

// Delete a savings entry
export const deleteSavingEntry = async (id: string): Promise<void> => {
  console.log("Deleting entry from Supabase:", id);
  
  try {
    const { error } = await supabase
      .from('savings_entries')
      .delete()
      .eq('id', id);
      
    if (error) {
      throw error;
    }
    
    console.log("Entry deleted successfully");
  } catch (error) {
    console.error("Error deleting entry:", error);
    throw error;
  }
};

// Update a savings entry
export const updateSavingEntry = async (id: string, updates: Partial<Omit<SavingEntry, 'id'>>): Promise<void> => {
  console.log("Updating entry in Supabase:", id, updates);
  
  try {
    const { error } = await supabase
      .from('savings_entries')
      .update(updates)
      .eq('id', id);
      
    if (error) {
      throw error;
    }
    
    console.log("Entry updated successfully");
  } catch (error) {
    console.error("Error updating entry:", error);
    throw error;
  }
};